import React from 'react';
import { Box, Typography, Grid, Card, CardContent, CardActionArea } from '@mui/material';
import { Link } from 'react-router-dom';
import { useTheme, alpha } from '@mui/material/styles';
import logo from '../../assets/fig_CROP.png';
import swanLogo from '../../assets/swanailogo.jpg';
import figAgentLogo from '../../assets/fig_agent.png';

/* Icons */
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import AssessmentIcon from '@mui/icons-material/Assessment';

const sitePaths = [
    {
        title: 'Finance',
        description: 'Manage budgets, expenses, and comprehensive financial tracking for school nutrition programs.',
        to: '/dashboard',
        Icon: AttachMoneyIcon,
        color: '#00897B',
    },
    {
        title: 'FIG Agent',
        description: 'AI-powered assistant providing intelligent insights and automated support for K12 nutrition planning.',
        to: '/figagent',
        color: '#5E35B1',
        logo: figAgentLogo,
    },
    {
        title: 'Invoice Scan (Mobile)',
        description: 'Capture invoice details instantly with mobile photo scanning and automatic data extraction.',
        to: '/invoice-mobilescan',
        Icon: QrCodeScannerIcon,
        color: '#F4511E',
    },
    {
        title: 'Mobile Inspection',
        description: 'Conduct on-site inspections with digital checklists and instant compliance reporting.',
        to: '/mobileinspection',
        Icon: FactCheckIcon,
        color: '#7B1FA2',
    },
    {
        title: 'Reports',
        description: 'Generate detailed financial reports and analytics for comprehensive program oversight.',
        to: '/finance-reports',
        Icon: AssessmentIcon,
        color: '#0277BD',
    },
];

const figProducts = [
    {
        title: 'FIG K12 Professional Development',
        description: 'Professional training and certification for K12 nutrition teams.',
        href: 'https://figk-12.com',
        color: '#1565C0',
        logo: logo,
    },
    {
        title: 'FIG POS',
        description: 'Point of Sale system for efficient meal service and transaction management.',
        href: 'https://pos.figk-12.com',
        color: '#EF6C00',
        logo: logo,
    },
    {
        title: 'Special Diets',
        description: 'Tools for allergens, restrictions, and compliant menu planning.',
        href: 'https://specialdiets.figk-12.com',
        color: '#C62828',
        logo: logo,
    },
    {
        title: 'Swan AI Labs',
        description: 'Cutting-edge AI research and development for educational technology.',
        href: 'https://swanailabs.com',
        color: '#283593',
        customLogo: swanLogo,
    },
    {
        title: 'FIG Group Purchasing Organization Management',
        description: 'Streamline procurement and vendor management for school nutrition programs.',
        href: 'https://datadock.figk-12.com',
        color: '#2E7D32',
        logo: logo,
    },
];

export default function Home() {
    const theme = useTheme();

    const getCardStyles = (color) => ({
        position: 'relative',
        borderRadius: 3,
        overflow: 'hidden',
        background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.5)}, ${alpha(theme.palette.background.paper, 0.3)})`,
        backdropFilter: 'blur(24px)',
        border: `1px solid ${alpha(color, 0.2)}`,
        color: theme.palette.text.primary,
        boxShadow: `
            0 6px 24px ${alpha(color, 0.12)},
            0 0 0 1px ${alpha(color, 0.08)}
        `,
        transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
        '&:active': {
            transform: 'scale(0.97)',
            transition: 'all 0.1s ease',
        },
        '&:hover': {
            transform: 'translateY(-10px) scale(1.02)',
            border: `1px solid ${alpha(color, 0.5)}`,
            boxShadow: `
                0 20px 56px ${alpha(color, 0.28)},
                0 0 100px ${alpha(color, 0.35)},
                0 0 0 1px ${alpha(color, 0.25)}
            `,
            background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.7)}, ${alpha(theme.palette.background.paper, 0.4)})`,
        },
    });

    const centerWrap = {
        height: '100%',
        display: 'grid',
        gridTemplateRows: '1fr auto auto auto',
        alignItems: 'center',
        justifyItems: 'center',
        p: { xs: 2, sm: 2.5 },
        gap: 1.2,
        textAlign: 'center',
    };

    const SitePathCard = ({ item }) => {
        const { title, description, to, Icon, color, logo: itemLogo } = item;

        return (
            <Grid item xs={12} sm={6} md={2.4} key={title}>
                <Card sx={getCardStyles(color)} aria-label={title}>
                    <Box sx={{ aspectRatio: '1 / 1', position: 'relative' }}>
                        <CardActionArea
                            component={Link}
                            to={to}
                            sx={{
                                position: 'absolute',
                                inset: 0,
                                borderRadius: 3,
                                '&.Mui-focusVisible': {
                                    outline: `3px solid ${alpha(color, 0.7)}`,
                                    outlineOffset: 2,
                                },
                            }}
                        >
                            <CardContent sx={centerWrap}>
                                {itemLogo ? (
                                    <Box
                                        sx={{
                                            position: 'relative',
                                            display: 'grid',
                                            placeItems: 'center',
                                            width: 'min(22vmin, 110px)',
                                            height: 'min(22vmin, 110px)',
                                            borderRadius: 3,
                                            background: `linear-gradient(135deg, ${alpha(color, 0.12)}, ${alpha(color, 0.05)})`,
                                            backdropFilter: 'blur(12px)',
                                            border: `1px solid ${alpha(color, 0.25)}`,
                                            boxShadow: `
                                                0 12px 40px ${alpha(color, 0.25)},
                                                0 0 80px ${alpha(color, 0.2)},
                                                inset 0 1px 0 ${alpha('#fff', 0.1)}
                                            `,
                                            transition: 'all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
                                            p: 2,
                                            '& img': {
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'contain',
                                                filter: `drop-shadow(0 4px 20px ${alpha(color, 0.4)})`,
                                                transition: 'all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
                                            },
                                            '.MuiCardActionArea-root:hover &': {
                                                transform: 'translateY(-6px) scale(1.08)',
                                                border: `1px solid ${alpha(color, 0.5)}`,
                                                background: `linear-gradient(135deg, ${alpha(color, 0.2)}, ${alpha(color, 0.1)})`,
                                                boxShadow: `
                                                    0 16px 56px ${alpha(color, 0.4)},
                                                    0 0 120px ${alpha(color, 0.35)},
                                                    inset 0 1px 0 ${alpha('#fff', 0.15)}
                                                `,
                                            },
                                            '.MuiCardActionArea-root:hover & img': {
                                                transform: 'scale(1.1)',
                                                filter: `drop-shadow(0 6px 28px ${alpha(color, 0.6)})`,
                                            },
                                        }}
                                    >
                                        <Box component="img" src={itemLogo} alt={title} />
                                    </Box>
                                ) : (
                                    <Box
                                        sx={{
                                            position: 'relative',
                                            display: 'grid',
                                            placeItems: 'center',
                                            width: 'min(22vmin, 110px)',
                                            height: 'min(22vmin, 110px)',
                                            borderRadius: 3,
                                            background: `linear-gradient(135deg, ${alpha(color, 0.12)}, ${alpha(color, 0.05)})`,
                                            backdropFilter: 'blur(12px)',
                                            border: `1px solid ${alpha(color, 0.25)}`,
                                            boxShadow: `
                                                0 12px 40px ${alpha(color, 0.25)},
                                                0 0 80px ${alpha(color, 0.2)},
                                                inset 0 1px 0 ${alpha('#fff', 0.1)}
                                            `,
                                            transition: 'all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
                                            '& svg': {
                                                fontSize: 'clamp(56px, 10vmin, 80px)',
                                                color: color,
                                                filter: `drop-shadow(0 4px 20px ${alpha(color, 0.4)})`,
                                                transition: 'all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
                                            },
                                            '.MuiCardActionArea-root:hover &': {
                                                transform: 'translateY(-6px) scale(1.08)',
                                                border: `1px solid ${alpha(color, 0.5)}`,
                                                background: `linear-gradient(135deg, ${alpha(color, 0.2)}, ${alpha(color, 0.1)})`,
                                                boxShadow: `
                                                    0 16px 56px ${alpha(color, 0.4)},
                                                    0 0 120px ${alpha(color, 0.35)},
                                                    inset 0 1px 0 ${alpha('#fff', 0.15)}
                                                `,
                                            },
                                            '.MuiCardActionArea-root:hover & svg': {
                                                transform: 'scale(1.1)',
                                                filter: `drop-shadow(0 6px 28px ${alpha(color, 0.6)})`,
                                            },
                                        }}
                                    >
                                        <Icon />
                                    </Box>
                                )}

                                <Typography
                                    sx={{
                                        mt: 0.5,
                                        fontWeight: 800,
                                        letterSpacing: 0.2,
                                        fontSize: { xs: 14, sm: 16 },
                                        transition: 'all 0.3s ease',
                                        '.MuiCardActionArea-root:hover &': {
                                            color: color,
                                            transform: 'scale(1.04)',
                                        },
                                    }}
                                >
                                    {title}
                                </Typography>

                                <Typography
                                    sx={{
                                        maxWidth: 400,
                                        opacity: 0.8,
                                        fontSize: { xs: 11, sm: 12 },
                                        lineHeight: 1.3,
                                        px: { xs: 0.5, sm: 0 },
                                        display: '-webkit-box',
                                        WebkitBoxOrient: 'vertical',
                                        WebkitLineClamp: 2,
                                        overflow: 'hidden',
                                        color: theme.palette.text.secondary,
                                        transition: 'all 0.3s ease',
                                        '.MuiCardActionArea-root:hover &': {
                                            opacity: 1,
                                        },
                                    }}
                                >
                                    {description}
                                </Typography>

                                <Box
                                    sx={{
                                        mt: 0.5,
                                        fontWeight: 700,
                                        textTransform: 'uppercase',
                                        letterSpacing: 1,
                                        fontSize: 10,
                                        px: 2.5,
                                        py: 0.5,
                                        borderRadius: 2,
                                        background: alpha(color, 0.12),
                                        border: `1px solid ${alpha(color, 0.25)}`,
                                        color: color,
                                        transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                                        '.MuiCardActionArea-root:hover &': {
                                            background: alpha(color, 0.22),
                                            border: `1px solid ${alpha(color, 0.5)}`,
                                            transform: 'scale(1.06)',
                                            boxShadow: `0 4px 20px ${alpha(color, 0.35)}`,
                                        },
                                    }}
                                >
                                    Launch
                                </Box>
                            </CardContent>
                        </CardActionArea>
                    </Box>
                </Card>
            </Grid>
        );
    };

    const ProductCard = ({ item }) => {
        const { title, description, href, color, logo: productLogo, customLogo } = item;

        return (
            <Grid item xs={12} sm={6} md={2.4} key={title}>
                <Card sx={getCardStyles(color)} aria-label={title}>
                    <Box sx={{ aspectRatio: '1 / 1', position: 'relative' }}>
                        <CardActionArea
                            component="a"
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                                position: 'absolute',
                                inset: 0,
                                borderRadius: 3,
                                '&.Mui-focusVisible': {
                                    outline: `3px solid ${alpha(color, 0.7)}`,
                                    outlineOffset: 2,
                                },
                            }}
                        >
                            <CardContent sx={centerWrap}>
                                <Box
                                    sx={{
                                        position: 'relative',
                                        display: 'grid',
                                        placeItems: 'center',
                                        width: 'min(22vmin, 110px)',
                                        height: 'min(22vmin, 110px)',
                                        borderRadius: 3,
                                        background: `linear-gradient(135deg, ${alpha(color, 0.12)}, ${alpha(color, 0.05)})`,
                                        backdropFilter: 'blur(12px)',
                                        border: `1px solid ${alpha(color, 0.25)}`,
                                        boxShadow: `
                                            0 12px 40px ${alpha(color, 0.25)},
                                            0 0 80px ${alpha(color, 0.2)},
                                            inset 0 1px 0 ${alpha('#fff', 0.1)}
                                        `,
                                        transition: 'all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
                                        p: 2,
                                        '& img': {
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'contain',
                                            filter: `drop-shadow(0 4px 20px ${alpha(color, 0.4)})`,
                                            transition: 'all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
                                        },
                                        '.MuiCardActionArea-root:hover &': {
                                            transform: 'translateY(-6px) scale(1.08)',
                                            border: `1px solid ${alpha(color, 0.5)}`,
                                            background: `linear-gradient(135deg, ${alpha(color, 0.2)}, ${alpha(color, 0.1)})`,
                                            boxShadow: `
                                                0 16px 56px ${alpha(color, 0.4)},
                                                0 0 120px ${alpha(color, 0.35)},
                                                inset 0 1px 0 ${alpha('#fff', 0.15)}
                                            `,
                                        },
                                        '.MuiCardActionArea-root:hover & img': {
                                            transform: 'scale(1.1)',
                                            filter: `drop-shadow(0 6px 28px ${alpha(color, 0.6)})`,
                                        },
                                    }}
                                >
                                    <Box component="img" src={customLogo || productLogo} alt={title} />
                                </Box>

                                <Typography
                                    sx={{
                                        mt: 0.5,
                                        fontWeight: 800,
                                        letterSpacing: 0.2,
                                        fontSize: { xs: 14, sm: 16 },
                                        transition: 'all 0.3s ease',
                                        '.MuiCardActionArea-root:hover &': {
                                            color: color,
                                            transform: 'scale(1.04)',
                                        },
                                    }}
                                >
                                    {title}
                                </Typography>

                                <Typography
                                    sx={{
                                        maxWidth: 400,
                                        opacity: 0.8,
                                        fontSize: { xs: 11, sm: 12 },
                                        lineHeight: 1.3,
                                        px: { xs: 0.5, sm: 0 },
                                        display: '-webkit-box',
                                        WebkitBoxOrient: 'vertical',
                                        WebkitLineClamp: 2,
                                        overflow: 'hidden',
                                        color: theme.palette.text.secondary,
                                        transition: 'all 0.3s ease',
                                        '.MuiCardActionArea-root:hover &': {
                                            opacity: 1,
                                        },
                                    }}
                                >
                                    {description}
                                </Typography>

                                <Box
                                    sx={{
                                        mt: 0.5,
                                        fontWeight: 700,
                                        textTransform: 'uppercase',
                                        letterSpacing: 1,
                                        fontSize: 10,
                                        px: 2.5,
                                        py: 0.5,
                                        borderRadius: 2,
                                        background: alpha(color, 0.12),
                                        border: `1px solid ${alpha(color, 0.25)}`,
                                        color: color,
                                        transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                                        '.MuiCardActionArea-root:hover &': {
                                            background: alpha(color, 0.22),
                                            border: `1px solid ${alpha(color, 0.5)}`,
                                            transform: 'scale(1.06)',
                                            boxShadow: `0 4px 20px ${alpha(color, 0.35)}`,
                                        },
                                    }}
                                >
                                    Visit
                                </Box>
                            </CardContent>
                        </CardActionArea>
                    </Box>
                </Card>
            </Grid>
        );
    };

    return (
        <Box sx={{ p: { xs: 3, sm: 4, md: 5 } }}>
            {/* Hero */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    background: `linear-gradient(135deg, ${alpha('#1A237E', 0.9)}, ${alpha('#283593', 0.8)})`,
                    backdropFilter: 'blur(20px)',
                    borderRadius: 3,
                    border: `1px solid ${alpha('#3949AB', 0.3)}`,
                    p: { xs: 3, sm: 5 },
                    mb: { xs: 5, sm: 7 },
                    boxShadow: `
                        0 16px 48px ${alpha('#1A237E', 0.4)},
                        0 0 80px ${alpha('#3949AB', 0.3)}
                    `,
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                <Box sx={{ position: 'relative', zIndex: 1 }}>
                    <Typography
                        variant="h2"
                        sx={{
                            fontWeight: 900,
                            color: '#fff',
                            fontSize: { xs: 32, sm: 48 },
                            textShadow: `0 4px 24px ${alpha('#3949AB', 0.6)}`,
                        }}
                    >
                        FIG Finance
                    </Typography>
                    <Typography
                        variant="h6"
                        sx={{
                            color: '#E8EAF6',
                            mt: 1.5,
                            fontSize: { xs: 15, sm: 18 },
                            textShadow: `0 2px 12px ${alpha('#000', 0.3)}`,
                        }}
                    >
                        Streamline your K12 nutrition financial management with insightful dashboards and AI-driven support.
                    </Typography>
                </Box>
                <Box
                    component="img"
                    src={logo}
                    alt="FIG Logo"
                    sx={{
                        height: { xs: 72, sm: 120 },
                        filter: `drop-shadow(0 8px 32px ${alpha('#3949AB', 0.5)})`,
                        position: 'relative',
                        zIndex: 1,
                    }}
                />
            </Box>

            {/* Site Paths Section */}
            <Box sx={{ mb: 6 }}>
                <Box sx={{ mb: 3, pb: 2, borderBottom: `2px solid ${alpha(theme.palette.divider, 0.1)}` }}>
                    <Typography
                        variant="h4"
                        sx={{
                            fontWeight: 900,
                            background: 'linear-gradient(135deg, #00897B 0%, #5E35B1 100%)',
                            backgroundClip: 'text',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            fontSize: { xs: 24, sm: 32 },
                        }}
                    >
                        Site Paths
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5, color: theme.palette.text.secondary, fontSize: { xs: 13, sm: 14 } }}>
                        Access your core financial and operational tools
                    </Typography>
                </Box>
                <Grid container spacing={3}>
                    {sitePaths.map((item) => (
                        <SitePathCard key={item.title} item={item} />
                    ))}
                </Grid>
            </Box>

            {/* FIG Products Section */}
            <Box>
                <Box sx={{ mb: 3, pb: 2, borderBottom: `2px solid ${alpha(theme.palette.divider, 0.1)}` }}>
                    <Typography
                        variant="h4"
                        sx={{
                            fontWeight: 900,
                            background: 'linear-gradient(135deg, #1565C0 0%, #C62828 100%)',
                            backgroundClip: 'text',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            fontSize: { xs: 24, sm: 32 },
                        }}
                    >
                        FIG Products
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5, color: theme.palette.text.secondary, fontSize: { xs: 13, sm: 14 } }}>
                        Explore our suite of educational nutrition solutions
                    </Typography>
                </Box>
                <Grid container spacing={3}>
                    {figProducts.map((item) => (
                        <ProductCard key={item.title} item={item} />
                    ))}
                </Grid>
            </Box>
        </Box>
    );
}